// src/styles/theme-mode.mjs
//
// Single owner of the theme mode. States:
//   'system' -> no [data-theme-mode] attribute; CSS follows prefers-color-scheme
//   'light'  -> [data-theme-mode="light"]
//   'dark'   -> [data-theme-mode="dark"]
//
// 'system' is represented by the ABSENCE of the attribute rather than a value,
// so the media query in variables.css.mjs does the work and there is nothing to
// keep in sync when the OS preference changes.

/*
 * ---------------------------------------------------------------------------
 * DEV-1166: following the OS preference is intentionally OFF for now.
 *
 * Dark mode was unreachable before this ticket, so no app had ever been
 * exercised in it. A source audit of all 23 Studio repos found 366 theme
 * findings (see DEV-1166-THEME-AUDIT.md) - Publisher, the section-front and
 * content editors, Spotlight, Keyword Manager and Alerts are all visibly broken
 * in dark. Defaulting to the OS would ship that to anyone whose machine prefers
 * dark, so dark is opt-in via studio-theme-toggle until those are fixed.
 *
 * TO TURN ON SYSTEM DEFAULT once the apps are compliant - two coordinated edits,
 * and they must ship together:
 *   1. Set SYSTEM_PREFERENCE_ENABLED = true below. That restores the 'system'
 *      option on the toggle and makes it the default.
 *   2. In ./variables.css.mjs, re-enable the OS rule marked
 *      "DEV-1166: OS PREFERENCE RULE".
 *
 * Users who explicitly picked Light or Dark keep that choice either way, because
 * an explicit preference is stored and an absent one is what "system" means.
 * ---------------------------------------------------------------------------
 */
export const SYSTEM_PREFERENCE_ENABLED = false;

export const THEME_STORAGE_KEY = 'studio.theme-mode';
export const THEME_MODES = SYSTEM_PREFERENCE_ENABLED
	? ['system', 'light', 'dark']
	: ['light', 'dark'];
export const THEME_CHANGE_EVENT = 'studio:theme-mode-change';

/** What an absent or unreadable preference means. */
const DEFAULT_MODE = SYSTEM_PREFERENCE_ENABLED ? 'system' : 'light';

const isMode = (v) => THEME_MODES.includes(v);

/**
 * The user's stored preference, or the default when unset/unavailable.
 * A stored 'system' from a build where it was enabled falls back to the default
 * rather than throwing, so toggling the flag off cannot strand anyone.
 */
export function getThemeMode() {
	try {
		const stored = globalThis.localStorage?.getItem(THEME_STORAGE_KEY);
		return isMode(stored) ? stored : DEFAULT_MODE;
	} catch {
		// Private mode / disabled storage.
		return DEFAULT_MODE;
	}
}

/** What the page is actually painting right now, after resolving 'system'. */
export function getResolvedTheme(mode = getThemeMode()) {
	if (mode === 'light' || mode === 'dark') return mode;
	// While SYSTEM_PREFERENCE_ENABLED is false the CSS has no OS rule, so an
	// unset preference paints light regardless of what the OS asks for. Report
	// that truthfully rather than what the OS prefers.
	if (!SYSTEM_PREFERENCE_ENABLED) return 'light';
	return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches
		? 'dark'
		: 'light';
}

/**
 * Apply a mode to the document. Exported separately from setThemeMode so it can
 * run before first paint without touching storage.
 */
export function applyThemeMode(mode) {
	const root = globalThis.document?.documentElement;
	if (!root) return;
	if (mode === 'light' || mode === 'dark') root.setAttribute('data-theme-mode', mode);
	else root.removeAttribute('data-theme-mode');
	syncThemeColorMeta(mode);
}

/**
 * Keep the browser chrome (address bar / installed-PWA title bar) in step with
 * the resolved theme. A manifest's theme_color cannot reference tokens or
 * react to [data-theme-mode], but a document-level meta overrides it.
 * Values mirror --surface-base light/dark.
 */
function syncThemeColorMeta(mode) {
	const doc = globalThis.document;
	if (!doc?.head) return;
	let meta = doc.head.querySelector('meta[name="theme-color"]:not([media])');
	if (!meta) {
		meta = doc.createElement('meta');
		meta.setAttribute('name', 'theme-color');
		doc.head.appendChild(meta);
	}
	meta.setAttribute('content', getResolvedTheme(mode) === 'dark' ? '#181b1f' : '#ffffff');
}

/** Persist and apply, then announce it so any mounted toggles re-render. */
export function setThemeMode(mode) {
	const next = isMode(mode) ? mode : DEFAULT_MODE;
	try {
		if (next === 'system') globalThis.localStorage?.removeItem(THEME_STORAGE_KEY);
		else globalThis.localStorage?.setItem(THEME_STORAGE_KEY, next);
	} catch {
		// Not persistable; still apply for this session.
	}
	applyThemeMode(next);
	globalThis.dispatchEvent?.(new CustomEvent(THEME_CHANGE_EVENT, {
		detail: { mode: next, resolved: getResolvedTheme(next) },
	}));
	return next;
}

/**
 * Re-apply the stored preference. Called at import time by inject-globals so the
 * correct theme is on the root element before the first paint - setting it later
 * (e.g. from a component's connectedCallback) shows a flash of the wrong theme.
 */
export function initThemeMode() {
	applyThemeMode(getThemeMode());
}
